import React, {useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {
    PanelHeader,
    SettingsPanel,
    TestId as MediaTestId,
} from '@pexip/media-components';
import {
    ActionsRow,
    ThemeProvider,
    ToggleSwitch,
    Text,
    FontVariant,
    Input,
} from '@pexip/components';

import {TestId} from '../../test/testIds';
import {config, useConfig} from '../config';

export const JoiningSettings: React.FC<{
    onBackClick: () => void;
}> = ({onBackClick}) => {
    const {t} = useTranslation();

    const [enforceAudioMute, setEnforceAudioMute] =
        useConfig('enforceAudioMute');
    const [enforceVideoMute, setEnforceVideoMute] =
        useConfig('enforceVideoMute');

    const [name, setName] = useState(config.get('displayName'));

    const handleNameBlur = () => {
        setName(config.get('displayName'));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);

        const displayName = e.target.value.trim();

        if (displayName.length) {
            config.set({key: 'displayName', value: displayName});
        }
    };

    return (
        <>
            <SettingsPanel
                headerContent={
                    <ThemeProvider colorScheme="light">
                        <PanelHeader
                            title={`${t('settings.joining', 'Joining')}`}
                            onBackClick={onBackClick}
                            backButtonTestId={MediaTestId.ButtonSettingsBack}
                        />
                    </ThemeProvider>
                }
            >
                <ThemeProvider colorScheme="light">
                    <Input
                        autoComplete="name"
                        value={name}
                        className="mb-2"
                        label={t('username.your-name', 'Your name')}
                        name="name"
                        onChange={handleNameChange}
                        onBlur={handleNameBlur}
                        placeholder={t(
                            'username.name-input-placeholder',
                            "e.g. 'David'",
                        )}
                        sizeModifier="medium"
                        data-testid={TestId.SettingsUserNameStepTextInput}
                    />
                    <Text
                        fontVariant={FontVariant.Small}
                        variant="tinted"
                        data-testid={
                            TestId.SettingsUserNameStepTextInputDisclaimer
                        }
                    >
                        <Trans i18nKey="settings.joining-name-disclaimer">
                            To update your display name during an ongoing call,
                            you will need to re-join the call.
                        </Trans>
                    </Text>
                    <ActionsRow
                        title={t(
                            'settings.enforce-mute-camera-by-default-title',
                            'Camera off when joining',
                        )}
                        subtitle={t(
                            'settings.enforce-mute-camera-by-default-desc',
                            'When you join the meeting, your camera will be off each time you join. You can turn the camera on at anytime during the meeting.',
                        )}
                        data-testid={TestId.ToggleEnforceVideoMuteRow}
                    >
                        <div style={{paddingInlineStart: '50px'}}>
                            <ToggleSwitch
                                onChange={isChecked => {
                                    setEnforceVideoMute(isChecked, true);
                                }}
                                label={t(
                                    'settings.enforce-mute-camera-by-default-title',
                                    'Camera off when joining',
                                )}
                                labelModifier="hidden"
                                name="enforce-video-mute"
                                data-testid={TestId.ToggleEnforceVideoMute}
                                checked={enforceVideoMute}
                            />
                        </div>
                    </ActionsRow>
                    <ActionsRow
                        title={t(
                            'settings.enforce-mute-microphone-by-default-title',
                            'Mute microphone when joining',
                        )}
                        subtitle={t(
                            'settings.enforce-mute-microphone-by-default-desc',
                            'When you join the meeting, your microphone will be muted each time you join. You can unmute your microphone at anytime during the meeting.',
                        )}
                        data-testid={TestId.ToggleEnforceAudioMuteRow}
                    >
                        <div style={{paddingInlineStart: '50px'}}>
                            <ToggleSwitch
                                onChange={isChecked => {
                                    setEnforceAudioMute(isChecked, true);
                                }}
                                label={t(
                                    'settings.enforce-mute-microphone-by-default-title',
                                    'Mute microphone when joining',
                                )}
                                labelModifier="hidden"
                                name="enforce-audio-mute"
                                data-testid={TestId.ToggleEnforceAudioMute}
                                checked={enforceAudioMute}
                            />
                        </div>
                    </ActionsRow>
                </ThemeProvider>
            </SettingsPanel>
        </>
    );
};
